import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  NoSymbolIcon,
  EyeIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { apiService, AffiliateMember, AffiliateMemberStatus } from '../services/api';
import AffiliateMemberCreateModal from './AffiliateMemberCreateModal';
import AffiliateMemberEditModal from './AffiliateMemberEditModal';
import ConfirmModal from './ConfirmModal';

interface AffiliateMemberManagementProps {
  onBack?: () => void;
  onViewMember?: (memberId: number) => void;
}

const AffiliateMemberManagement: React.FC<AffiliateMemberManagementProps> = ({ onBack, onViewMember }) => {
  const [members, setMembers] = useState<AffiliateMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState<AffiliateMember | null>(null);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<AffiliateMember | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getAffiliateMembers({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter as AffiliateMemberStatus : undefined,
      });
      setMembers(response.members || []);
      setTotal(response.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch affiliate members:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleCreate = () => {
    setIsCreateModalVisible(true);
  };

  const handleEdit = (member: AffiliateMember) => {
    setSelectedMember(member);
    setIsEditModalVisible(true);
  };

  const handleView = (member: AffiliateMember) => {
    if (onViewMember) {
      onViewMember(member.id);
    }
  };

  const handleDelete = (member: AffiliateMember) => {
    setMemberToDelete(member);
    setIsDeleteConfirmVisible(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;
    
    try {
      await apiService.updateAffiliateMember(memberToDelete.id, {
        status: AffiliateMemberStatus.INACTIVE,
      });
      setIsDeleteConfirmVisible(false);
      setMemberToDelete(null);
      fetchMembers();
    } catch (error) {
      console.error('Failed to deactivate affiliate member:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to deactivate affiliate member. Please try again.';
      alert(errorMessage);
    }
  };

  const cancelDelete = () => {
    setIsDeleteConfirmVisible(false);
    setMemberToDelete(null);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalVisible(false);
    fetchMembers();
  };

  const handleEditSuccess = () => {
    setIsEditModalVisible(false);
    setSelectedMember(null);
    fetchMembers();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: AffiliateMemberStatus) => {
    switch (status) {
      case AffiliateMemberStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case AffiliateMemberStatus.INACTIVE:
        return 'bg-gray-100 text-gray-800';
      case AffiliateMemberStatus.BANNED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Affiliate Member Management</h1>
        </div>
        <div className="text-sm text-gray-500">
          Total Members: {total}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by code, user email, or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="all">All</option>
              <option value={AffiliateMemberStatus.ACTIVE}>Active</option>
              <option value={AffiliateMemberStatus.INACTIVE}>Inactive</option>
              <option value={AffiliateMemberStatus.BANNED}>Banned</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <button
            onClick={handleCreate}
            className="bg-imola-primary hover:bg-imola-primary/80 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add Member
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wallet Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-left text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-left text-gray-500">
                    No affiliate members found
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                        {member.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      {member.code ? (
                        <div className="text-sm font-medium text-gray-900 font-mono bg-blue-50 px-2 py-1 rounded border border-blue-200">
                          {member.code}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      {member.slug ? (
                        <div className="text-sm text-gray-900 font-mono bg-green-50 px-2 py-1 rounded border border-green-200">
                          {member.slug}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      {member.user ? (
                        <>
                          <div className="text-sm font-medium text-gray-900">
                            {member.user.name || 'No name'}
                          </div>
                          <div className="text-xs text-gray-500">{member.user.email}</div>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">No user linked</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(member.walletBalance)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(member.status)}`}>
                        {member.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div className="text-sm text-gray-900">
                        {new Date(member.createdAt).toLocaleDateString('id-ID', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(member)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(member)}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {member.status !== AffiliateMemberStatus.INACTIVE && (
                          <button
                            onClick={() => handleDelete(member)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Deactivate"
                          >
                            <NoSymbolIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                disabled={currentPage >= Math.ceil(total / pageSize)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, total)}
                  </span>{' '}
                  of <span className="font-medium">{total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(total / pageSize)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AffiliateMemberCreateModal
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      <AffiliateMemberEditModal
        visible={isEditModalVisible}
        member={selectedMember}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedMember(null);
        }}
        onSuccess={handleEditSuccess}
      />

      <ConfirmModal
        visible={isDeleteConfirmVisible}
        title="Deactivate Affiliate Member"
        message={memberToDelete ? `Are you sure you want to deactivate this affiliate member?` : ''}
        confirmText="Deactivate"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default AffiliateMemberManagement;

